import { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Eraser } from 'lucide-react';

function getThemeColors() {
  const styles = getComputedStyle(document.documentElement);
  const bg = styles.getPropertyValue('--background').trim();
  const fg = styles.getPropertyValue('--foreground').trim();
  return {
    fillStyle: bg ? `hsl(${bg})` : '#18181b',
    strokeStyle: fg ? `hsl(${fg})` : '#ffffff',
  };
}

export default function SignaturePad({ onSave, width = 400, height = 200, className = '' }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const colors = getThemeColors();
    ctx.fillStyle = colors.fillStyle;
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = colors.strokeStyle;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [width, height]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setDrawing(true);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = () => {
    setDrawing(false);
    if (hasDrawn && onSave) {
      onSave(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const colors = getThemeColors();
    ctx.fillStyle = colors.fillStyle;
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = colors.strokeStyle;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    setHasDrawn(false);
    if (onSave) onSave(null);
  }, [width, height, onSave]);

  return (
    <div className={className}>
      <div className="relative border border-input rounded-lg overflow-hidden">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="w-full touch-none"
          style={{ maxHeight: `${height}px` }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        {!hasDrawn && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-muted-foreground text-sm">Sign here</span>
          </div>
        )}
      </div>
      <Button variant="outline" size="sm" onClick={clear}
        className="mt-2 border-input text-muted-foreground h-7 text-xs gap-1">
        <Eraser className="w-3 h-3" /> Clear
      </Button>
    </div>
  );
}
